import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

/**
 * MiniLoader Component
 * @param {Object} props
 * @param {number} [props.size=16] - Size of the loader in pixels
 * @param {string} [props.color='#6366f1'] - Color of the loader
 * @param {Object} [props.style={}] - Additional CSS styles
 * @param {boolean} [props.overlay=false] - Whether to render as overlay
 * @param {'spinner'|'dots'|'pulse'|'ai'} [props.variant='spinner'] - Type of loader animation
 * @param {'slow'|'normal'|'fast'} [props.speed='normal'] - Animation speed
 */
const MiniLoader = ({ 
  size = 16, 
  color = '#6366f1', 
  style = {}, 
  overlay = false, 
  variant = 'spinner', 
  speed = 'normal' 
}) => {
  const speedMap = {
    slow: 2,
    normal: 1,
    fast: 0.5
  };

  const animationDuration = speedMap[speed];

  const renderSpinner = () => (
    <FontAwesomeIcon 
      icon={faSpinner} 
      spin 
      style={{ 
        fontSize: size, 
        color: color,
        animationDuration: `${animationDuration}s`
      }} 
    />
  );

  const renderDots = () => (
    <div className="dots-loader" style={{ fontSize: size }}>
      <span className="dot" style={{ backgroundColor: color, animationDelay: '0s' }}></span>
      <span className="dot" style={{ backgroundColor: color, animationDelay: '0.2s' }}></span>
      <span className="dot" style={{ backgroundColor: color, animationDelay: '0.4s' }}></span>
    </div>
  );

  const renderPulse = () => (
    <div className="pulse-loader" style={{ width: size, height: size }}>
      <div className="pulse-circle" style={{ backgroundColor: color }}></div>
    </div>
  );

  /** AI-style: neural bars + soft core pulse (no external assets). */
  const renderAI = () => {
    const barW = Math.max(3, Math.round(size * 0.16));
    const dur = `${animationDuration * 1.15}s`;
    const glow = `0 0 ${Math.max(4, Math.round(size * 0.25))}px ${color}66`;
    return (
      <div
        className="ai-loader"
        style={{
          height: size,
          fontSize: size,
          gap: Math.max(3, Math.round(size * 0.14)),
          color,
        }}
        aria-hidden
      >
        <div className="ai-loader__core" />
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="ai-loader__bar"
            style={{
              width: barW,
              backgroundColor: color,
              boxShadow: glow,
              animationDuration: dur,
              animationDelay: `${i * animationDuration * 0.11}s`,
            }}
          />
        ))}
      </div>
    );
  };

  const renderLoader = () => {
    switch (variant) {
      case 'dots':
        return renderDots();
      case 'pulse':
        return renderPulse();
      case 'ai':
        return renderAI();
      case 'spinner':
      default:
        return renderSpinner();
    }
  };

  if (overlay) {
    return (
      <div 
        className="mini-loader-overlay" 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(2px)',
          transition: 'all 0.3s ease',
          ...style
        }}
      >
        {renderLoader()}
      </div>
    );
  }

  return (
    <>
      <div 
        className="mini-loader" 
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...style
        }}
      >
        {renderLoader()}
      </div>
      
      <style jsx>{`
        /* Dots Animation */
        .dots-loader {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .dots-loader .dot {
          width: 0.4em;
          height: 0.4em;
          border-radius: 50%;
          animation: dots-bounce 1.4s ease-in-out infinite both;
        }

        @keyframes dots-bounce {
          0%, 80%, 100% {
            transform: scale(0);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }

        /* Pulse Animation */
        .pulse-loader {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .pulse-loader .pulse-circle {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          animation: pulse-animation 1.2s ease-in-out infinite;
        }

        @keyframes pulse-animation {
          0% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          50% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
        }

        /* AI / neural-style loader */
        .ai-loader {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .ai-loader__core {
          position: absolute;
          width: 0.42em;
          height: 0.42em;
          left: 50%;
          top: 50%;
          margin-left: -0.21em;
          margin-top: -0.21em;
          font-size: inherit;
          border-radius: 50%;
          background: currentColor;
          opacity: 0.35;
          animation: ai-core-pulse 1.4s ease-in-out infinite;
          box-shadow: 0 0 0.28em currentColor;
        }

        @keyframes ai-core-pulse {
          0%,
          100% {
            transform: scale(0.85);
            opacity: 0.25;
          }
          50% {
            transform: scale(1.15);
            opacity: 0.55;
          }
        }

        .ai-loader__bar {
          align-self: flex-end;
          height: 70%;
          border-radius: 4px;
          transform-origin: center bottom;
          opacity: 0.85;
          animation: ai-bar-wave ease-in-out infinite;
        }

        @keyframes ai-bar-wave {
          0%,
          100% {
            transform: scaleY(0.28);
            opacity: 0.45;
          }
          50% {
            transform: scaleY(1);
            opacity: 1;
          }
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .dots-loader {
            gap: 3px;
          }
          
          .dots-loader .dot {
            width: 0.35em;
            height: 0.35em;
          }
        }
      `}</style>
    </>
  );
};

// PropTypes for type checking and documentation
MiniLoader.propTypes = {
  size: PropTypes.number,
  color: PropTypes.string,
  style: PropTypes.object,
  overlay: PropTypes.bool,
  variant: PropTypes.oneOf(['spinner', 'dots', 'pulse', 'ai']),
  speed: PropTypes.oneOf(['slow', 'normal', 'fast'])
};

// Default props
MiniLoader.defaultProps = {
  size: 16,
  color: '#6366f1',
  style: {},
  overlay: false,
  variant: 'spinner',
  speed: 'normal'
};

export default MiniLoader; 